import { getUsableImages } from "../placeholder-image.js";
import { toggle } from "../utils/ui-helpers.js";
import { updatePaymentPlanSelectors } from "../payment-selector.js";
import { getTodayUTC } from "../utils/date-utils.js";
import { elById, qsAll, qs, dom } from "../utils/dom.js";
import { handleInputChange } from "../event-handlers.js";
import { getClientData, isMultisiteEnabled } from "../state.js";
import * as discountComponents from "./discount.js";

// const THUMBNAIL_SRC = 'https://cdn.anytimebooking.io/unit/';

// Wrapper
const summaryWrapper  = elById('atb-summary');

// Unit / Sub-unit / Address details section
let unitSection = {
    thumbnail:  null,
    name:       null,
    site:       null,
    subUnit:    null,
    location:   null
}

// Check In / Out / Length of stay text
let datesSection = {
    checkInLabel:  null,

    checkOutRow:   null,
    checkOutLabel: null,

    periodRow:     null,
    periodText:    null
}

let accordions = {
    'base-booking': null,
    extras:       null
}

let totalCostSection = {
    totalDepositRow: null,
    grandTotalRow  : null
}

// Discount section
let discountRow   = summaryWrapper.querySelector('#atb-summary-discount-code-row');

/**
 * Sets the text content of a heading, falling back to a default so the
 * heading is never left empty for assistive technology.
 * @param {HTMLElement} el
 * @param {string} text
 * @param {string} defaultText
 */
function setHeadingText(el, text, defaultText) {
    el.textContent = text || defaultText;
}

// The current checkout summary - this changes as bookings are added
let currentSummary = null;


export function init() {    
    getActiveBookingSummary();
    attachEventHandlers();
}

/**
 * Gets the active booking summary component - needed by the "multiple booking module" to resync the booking display when a booking is added/deleted
 */
export function getActiveBookingSummary() {
    currentSummary = qs('[data-active-booking]');
    if (currentSummary) {
        getSummarySections(currentSummary);          
    }
}


function attachEventHandlers() {
    summaryWrapper.addEventListener('change', (event) => {
        const paymentSelectorInput = event.target.closest('input[data-input="payment_select"]');
        if (!paymentSelectorInput)
            return;

        handleInputChange(paymentSelectorInput);
    });
}

/**
 * Retrieves each DOM element inside of a booking summary component given it's root
 * @param {HTMLElement} currentSummaryRoot 
 */
function getSummarySections(currentSummaryRoot) {    
    unitSection = {
        thumbnail: currentSummaryRoot.querySelector('[data-checkout-thumbnail]'),
        name:      currentSummaryRoot.querySelector('[data-summary-unit-name]'),
        site:      currentSummaryRoot.querySelector('[data-summary-unit-site]'),
        subUnit:   currentSummaryRoot.querySelector('[data-summary-sub-unit-name]'),
        location:  currentSummaryRoot.querySelector('[data-summary-unit-location]')
    }

    datesSection = {
        checkInLabel:  currentSummaryRoot.querySelector('[data-summary-arrival-date]'),

        checkOutRow:   currentSummaryRoot.querySelector('[data-summary-departure-row]'),
        checkOutLabel: currentSummaryRoot.querySelector('[data-summary-departure-date]'),

        periodRow:     currentSummaryRoot.querySelector('[data-summary-period-row]'),
        periodText:    currentSummaryRoot.querySelector('[data-summary-period]')
    }    

    accordions = {
        'base-booking': currentSummaryRoot.querySelector('#atb-summary-base-booking-accordion'),
        extras        : currentSummaryRoot.querySelector('#atb-summary-extras-accordion')
    }

    totalCostSection = {
        totalDepositRow: qs('#atb-total-to-pay', summaryWrapper),
        grandTotalRow:   qs('#atb-total-cost', summaryWrapper)
    }

    discountRow = currentSummaryRoot.querySelector('#atb-summary-discount-code-row');
}


// TODO: this function needs to be broken up into smaller UI updating functions that can be called individually
// Also remove JQuery dependency
export function updateSummary(costs) {
    const baseRow         = elById('atb-summary-base-row');
    const normalTotalRow  = elById('atb-summary-normal-total-row');
    const discountRows    = qsAll('.atb-summary-discount-row');
    const configEl        = elById('atb-config');

    if (configEl)
        configEl.setAttribute('data-free_of_charge', costs.freeOfCharge ? 1 : 0);
    
    // Base row
    toggle(baseRow, parseFloat(costs.baseUnformatted) > 0);

    // Discount
    const hasDiscount = costs.discountUnformatted > 0;
    discountRows.forEach(row => toggle(row, hasDiscount, 'flex'));

    if (hasDiscount) {
        const discountText =
            costs.isPercentageDiscount && costs.discountPercentage > 0
                ? `${costs.discountPercentage}%`
                : `${costs.discount}`;

        qs('.atb-chip.discount').innerHTML = `${discountText} OFF`;
    }

    discountComponents.update(costs.discountCode ?? '', hasDiscount);

    // Normal total (strikethrough)
    const showNormalTotal =
        costs.discountUnformatted > 0 ||
        costs.agentUnformatted > 0;

    toggle(normalTotalRow, showNormalTotal);
    
    elById('atb-summary-cost-before-discount').innerHTML =
        showNormalTotal ? formatPriceString(costs.totalNormal) : '';

    // VAT
    const vatElement = elById('atb-summary-total-vat-cost');
    if (vatElement) {
        elById('atb-summary-total-net-cost').innerHTML = costs.totalNetFormatted;
        vatElement.innerHTML = costs.totalVatFormatted;
    }

    // Totals
    elById('atb-summary-label-cost').innerHTML = costs.total;
    elById('atb-summary-discount-cost').innerHTML = costs.discount;

    qsAll('.atb-pay-total-cost')
        .forEach(el => el.innerHTML = formatPriceString(costs.total));

    const totalToPayEl = elById('atb-summary-total-to-pay-cost');
    if (totalToPayEl)
        totalToPayEl.innerHTML = formatPriceString(costs.totalToPay);

    // Deposit
    const depositCosts = {
        deposit           : formatPriceString(costs.deposit),
        depositUnformatted: costs.depositUnformatted,
        balance           : formatPriceString(costs.balance),
        balanceDueDate    : costs.balanceDueDate,
        balanceOnArrival  : costs.balanceOnArrival,
        balanceType       : costs.balanceType
    };

    updatePaymentPlanSelectors(!costs.fullOnly, depositCosts);

    // Party breakdown
    const partyBreakdown = costs.partyMembersBreakdown;
    const partySize       = costs.partySize;
    const extrasBreakdown = costs.addonsBreakdown;

    const basePrice = formatPriceString(costs.base);
    const extrasPrice = formatPriceString(costs.addons);

    // Read once from the base-booking element as the single source of truth
    const useCondensedBreakdown = parseIntb10(accordions['base-booking']?.dataset.condensedBreakdown) === 1;

    const baseBreakdown = (!partyBreakdown || partyBreakdown.length === 0)
        ? createPartySizeSummaryArray(parseIntb10(partySize))
        : partyBreakdown;

    const allBreakdowns = {
        baseBooking: baseBreakdown,
        extras: extrasBreakdown
    };

    buildCostAccordion('base-booking', baseBreakdown, basePrice, allBreakdowns, useCondensedBreakdown);
    buildCostAccordion('extras', extrasBreakdown, extrasPrice, allBreakdowns, useCondensedBreakdown);

    // Update total booking costs - significant only to multiple bookings
    updateGrandTotalsUI({
        allBookingsTotal                : costs.allBookingsTotal,
        allBookingsTotalToPay           : costs.allBookingsTotalToPay,
        allBookingsTotalToPayUnformatted: costs.allBookingsTotalToPayUnformatted
    });
}

// Update the grand-totals - generally only applies when dealing with multi-bookings
// TODO: maybe do this on condition that user has made more than 1 booking
function updateGrandTotalsUI({allBookingsTotal, allBookingsTotalToPay, allBookingsTotalToPayUnformatted}) {
    const grandTotalCostEls = qsAll('.atb-summary-grand-total-cost');
    const totalToPayTodayEls = qsAll('.atb-summary-total-to-pay-cost');

    grandTotalCostEls.map(el => el.innerHTML = allBookingsTotal);

    totalToPayTodayEls.map(el => 
        el.innerHTML = allBookingsTotalToPayUnformatted > 0 
            ? allBookingsTotalToPay 
            : allBookingsTotal
    );

    toggle(totalCostSection.totalDepositRow, allBookingsTotalToPayUnformatted > 0, 'flex');
}

/**
 *
 * @param {object} unitObj
 * @param {string} unitObj.id
 * @param {string} unitObj.name
 * @param {number} unitObj.occupancy
 * @param {string} unitObj.address
 * @param {string} [unitObj.siteName] - name of the site the unit belongs to (only shown when multisite_module is enabled)
 * @returns
 */
export const updateSummaryUnitUI = ({
    id,
    name,
    occupancy,
    address,
    siteName
}) => {
    // Update the validation text that appears under party member steppers
    // TODO: Don't think this belongs here but it'll stay fornow
    const maxOccEl = elById('atb-valid-max-occupancy');
    if (maxOccEl) maxOccEl.textContent = occupancy;
    
    const unitThumbnailSrc = getUnitThumbnailSrcById(id);
    setUnitThumbnailAsync({
        imageSrc: unitThumbnailSrc,
        imageAlt: `Image of ${name}`
    });

    setHeadingText(unitSection.name, name, "Booking Summary");

    const thumbnailContainer = currentSummary.querySelector('[data-checkout-thumbmail-container]')
    if (thumbnailContainer) {
        thumbnailContainer.style.display = '';
    }

    updateSummaryUnitDetailsUI(address);
    updateSummaryUnitSiteUI(siteName);
}


export const resetSummaryHead = () => {
    updateSummaryUnitDetailsUI('', false);
    updateSummarySubUnitUI('', false);
    updateSummaryUnitSiteUI('');
    resetSummaryDatesUI();
    toggleThumbnailWrapper(false);
}

// Functions
export const toggleThumbnailWrapper = (doShow = true) => {
    toggle(unitSection.thumbnail.parentElement, doShow);
}

export const updateSummaryThumbnailAttrs = (imageAttrsObj) => {    
    Object.keys(imageAttrsObj).forEach(key => unitSection.thumbnail.setAttribute(key, imageAttrsObj[key]));
}

export const updateSummaryUnitDetailsUI = (unitLocation, doShow = true) => {
    setHeadingText(unitSection.location, unitLocation, "");
    toggle(unitSection.location, doShow);
    toggle(unitSection.name, doShow);
}

export const updateSummarySubUnitUI = (subUnitName, doShow = true) => {
    setHeadingText(unitSection.subUnit, subUnitName, "Sub-unit");
    toggle(unitSection.subUnit, doShow);
}

/**
 * Updates the site name shown in the booking summary - only shown when the client has the multisite module enabled
 * @param {string} siteName
 */
export const updateSummaryUnitSiteUI = (siteName = '') => {
    setHeadingText(unitSection.site, siteName, "Site");
    toggle(unitSection.site, isMultisiteEnabled() && !!siteName);
}

export const updateSummaryDatesText = (checkIn, checkOut) => {
    datesSection.checkInLabel.textContent  = checkIn;
    datesSection.checkOutLabel.textContent = checkOut;
}

export const resetSummaryDatesUI = () => {
    console.log("Resetting summary dates UI");
    datesSection.checkInLabel.textContent  = "";
    datesSection.checkOutLabel.textContent = "";
    datesSection.periodText.textContent    = "";
}

export const updateSummaryDepartureDate = (departureDate, doShow = true) => {
    datesSection.checkOutLabel.textContent = departureDate;
    toggle(datesSection.checkOutRow, doShow);
    
    // Show summary discount code box
    toggle(discountRow, doShow);
};

export const updateSummaryPeriodText = (period) => {
    datesSection.periodText.textContent = period;
}

export const updateSummaryPeriodUI = (period, doShow = true) => {
    updateSummaryPeriodText(period);
    toggle(datesSection.periodRow, doShow);
}

const buildCostAccordion = (summaryAccordionName, breakdown, totalPriceString, allBreakdowns = {}, useCondensedBreakdown = true) => {
    const accordionElement = accordions[summaryAccordionName];
    const accordionContent = accordionElement.querySelector('.accordion-body');
    const priceElement = accordionElement.querySelector(
        `#atb-summary-${summaryAccordionName}-accordion-price`
    );

    if (!useCondensedBreakdown) {
        // Separate accordions — show both, each with their own data
        if (!breakdown || breakdown.length === 0) {
            accordionContent.innerHTML = '';
            accordionElement.classList.add('d-none');
            return;
        }

        accordionElement.classList.remove('d-none');
        accordionContent.innerHTML = '';

        const currencySymbol = getClientData().uiData.currency;

        if (summaryAccordionName === 'base-booking') {
            buildPartyMembersAccordion(breakdown, accordionContent, currencySymbol);
        } else if (summaryAccordionName === 'extras') {
            buildExtrasAccordion(breakdown, accordionContent, currencySymbol);
        }

    } else {
        // Combined — render everything into base-booking, hide extras
        if (summaryAccordionName === 'extras') {
            accordionElement.classList.add('d-none');
            return;
        }

        accordionContent.innerHTML = '';
        accordionElement.classList.remove('d-none');

        const currencySymbol = getClientData().uiData.currency;
        buildCombinedAccordion(
            allBreakdowns.baseBooking,
            allBreakdowns.extras,
            accordionContent,
            currencySymbol
        );
    }

    if (priceElement) {
        priceElement.innerHTML = totalPriceString;
    }
};


const buildPartyMembersAccordion = (partyMembersPriceBreakdown, accordionContentElement, currencySymbol = '&pound;') => {
    const partyMemberBreakdown = formatBaseBookingData(partyMembersPriceBreakdown);
    Object.values(partyMemberBreakdown).forEach(item => {
        const { name, quantity, unitCost, isIncluded } = item;
        const totalCost = unitCost * quantity;
        const isFree = totalCost === 0;

        const label = isIncluded
            ? 'Included'
            : isFree
                ? renderZeroCostString(3)
                : null;

        const displayCost = (!isIncluded && !isFree)
            ? formatPriceString(`${currencySymbol}${totalCost.toFixed(2)}`)
            : null;

        const nameFormatted =
            capitaliseFirstLetter(
                quantity > 1 ? pluralise(name) : depluralise(name)
            );

        const row = createMiniAccordionRow(
            quantity,
            nameFormatted,
            displayCost,
            isIncluded || isFree,
            label,
            0
        );

        accordionContentElement.append(row);
    });
}

const buildExtrasAccordion = (extrasPriceBreakdown, accordionContentElement, currencySymbol) => {
    // const skippable = { 'thl-url' }
    extrasPriceBreakdown.map(extra => {
        const { name, qty:quantity, cost:costString, cost_unformatted:costRaw, zero_cost_display: zeroCostDisplayStr } = extra;

        const isFree = parseFloat(costRaw) < 1;
        let formattedZeroCostStr = isFree ? renderZeroCostString(zeroCostDisplayStr) : 0;
        
        //const costFormatted = `${currencySymbol}${costString}`;
        const row = createMiniAccordionRow(quantity, name, costString, isFree, formattedZeroCostStr);
        accordionContentElement.append(row); 
    });
}

const normalisePartyMemberItem = (item, currencySymbol = '&pound;') => {
    const { name, quantity, unitCost, isIncluded } = item;
    const totalCost = unitCost * quantity;
    const isFree = totalCost === 0;

    const label = isIncluded
        ? 'Included'
        : isFree
            ? renderZeroCostString(3)
            : null;

    const displayCost = (!isIncluded && !isFree)
        ? formatPriceString(`${currencySymbol}${totalCost.toFixed(2)}`)
        : null;

    const nameFormatted = capitaliseFirstLetter(
        quantity > 1 ? pluralise(name) : depluralise(name)
    );

    return { quantity, name: nameFormatted, cost: displayCost, isFree: isIncluded || isFree, label, indent: 0 };
}

const normaliseExtraItem = (item) => {
    const { name, qty: quantity, cost: costString, cost_unformatted: costRaw, zero_cost_display: zeroCostDisplayStr } = item;
    const isFree = parseFloat(costRaw) < 1;
    const label = isFree ? renderZeroCostString(zeroCostDisplayStr) : null;

    return { quantity, name, cost: costString, isFree, label, indent: 0 };
}

const buildCombinedAccordion = (partyMembersPriceBreakdown, extrasPriceBreakdown, accordionContentElement, currencySymbol = '&pound;') => {
    const partyMemberBreakdown = formatBaseBookingData(partyMembersPriceBreakdown);

    const normalisedPartyMembers = Object.values(partyMemberBreakdown).map(item => normalisePartyMemberItem(item, currencySymbol));
    const normalisedExtras = extrasPriceBreakdown.map(normaliseExtraItem);

    [...normalisedPartyMembers, ...normalisedExtras].forEach(({ quantity, name, cost, isFree, label, indent }) => {
        const row = createMiniAccordionRow(quantity, name, cost, isFree, label, indent);
        accordionContentElement.append(row);
    });
}


const createMiniAccordionRow = (
    quantity, 
    itemName, 
    price, 
    isIncluded, 
    zeroCostString = 'Included', 
    showQuantity = 1
) => {    
    const formattedPrice = formatPriceString(price);
    const itemValue      = isIncluded ? zeroCostString : formattedPrice;
    
    let itemRowName = '';

    // if quantity is not undefined/null and over 1, include it in the item row name
    itemRowName += quantity && quantity > showQuantity ? `${quantity} ` : "";
    itemRowName += itemName;

    const row = document.createElement('div');
    row.className = 'row text-capitalize atb-text-secondary-color mb-1';
    row.innerHTML = `
            <div class="col-6 px-0">${itemRowName}</div>
            <div class="col-6 text-end px-0">${itemValue}</div>
    `;

    return row;
}


// format party member data for display in "base booking" accordion
const formatBaseBookingData = (partyMembersPriceBreakdown) => {
    const partyMemberBreakdown = {};
    // Loop over every party member, building quantity, labels and price columns for each party member
    partyMembersPriceBreakdown.forEach(pm => {
        const {
            name,
            calc_include,
            cost_unformatted,   // numeric
            cost               // numeric fallback
        } = pm;

        // If the party member is included in the price of the booking, diffrentiate it from those that are not in the key/value table
        const isIncluded = Number(calc_include) === 1;

        const unitCost =
            typeof cost_unformatted === 'number'
                ? cost_unformatted
                : typeof cost === 'number'
                    ? cost
                    : 0;

        const key = `${name}${isIncluded ? '-inc' : '-paid'}`;

        if (!partyMemberBreakdown[key]) {
            partyMemberBreakdown[key] = {
                name,
                quantity: 0,
                unitCost,
                isIncluded
            };
        }
        // Add to the quantity column if the party member has already been added
        partyMemberBreakdown[key].quantity += 1;
    });

    return partyMemberBreakdown;
}


/**
 * Attri
 * @param {Object} imageAttrsObj - Object containing image attributes {
 *  imageSrc: string,
 *  imageAlt: string }
 */
export async function setUnitThumbnailAsync(imageAttrsObj = null) {
    console.log('Obtaining non-placeholder images');
    const imageData = await getUnitThumbnailData('[data-checkout-thumbnail]', imageAttrsObj);
    if (imageData != null) {
        updateSummaryThumbnailAttrs(imageData);
    } else {
        toggleThumbnailWrapper(false);
    }
}


async function getUnitThumbnailData(selector, imageAttrsObj = null) {    
    imageAttrsObj = imageAttrsObj ?? getAttributesBySelector(selector);
    const { imageSrc, imageAlt } = imageAttrsObj;
    
    const images = [ imageSrc ];
    const imageResponse = await getUsableImages(images);

    return imageResponse.length == 0 
        ? null : 
        { src: imageSrc, alt: imageAlt };    
}


/**
 * Create an array of "party-size" booking summary items (for the accordions and booking session) of "party-size" length
 * Used to emulate the party member data objects for ease of rendering the "party-size" row in the "base booking" accordion residing in the booking summary
 * @param {number} partySize an integer representing the size of the party
 * @param {*} currencySymbol defaulting to £ british sterling
 * @returns {array} an array of objects emulating the way "party member" objects are returned from session
 */
const createPartySizeSummaryArray = (partySize, currencySymbol = '&pound;') => {
    // create an array of "party-size" booking summary items (for the accordions and booking session) using the "partySize" int
    const array = Array.from({length: partySize}, (_, i) => i + 1);

    return array.map(i => {
        return {
            arrival_date: getTodayUTC().toISOString(),
            name: "Party size",
            calc_include: 0,
            cost: `${currencySymbol}0.00`, 
            cost_unformatted: 0,
            cost_normal: 0,
            cost_normal_unformatted: 0,
            id: 0,
            position: 0,
            show_breakdown: 1,            
            qty: i,                        
            unit_id: 0,
            unit_name: "",            
            time: "",            
        }
    });
}
